import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import MainLayout from '../../components/layout/MainLayout';
import infrastructureMetricApi from '../../services/api/infrastructure-metric.api';
import kubernetesMetricApi from '../../services/api/kubernetes-metric.api';
import { hostApi } from '../../services/api/host.api';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';

type MetricSource = 'infrastructure' | 'kubernetes';

export default function MetricsPage() {
  const { t } = useTranslation(['metrics', 'common']);

  // Metric source selection
  const [metricSource, setMetricSource] = useState<MetricSource>('infrastructure');

  // Infrastructure metrics state
  const [hosts, setHosts] = useState<any[]>([]);
  const [selectedHost, setSelectedHost] = useState('');
  const [infraMetrics, setInfraMetrics] = useState<any>({});

  // Kubernetes metrics state
  const [containers, setContainers] = useState<any[]>([]);
  const [selectedContainer, setSelectedContainer] = useState('');
  const [k8sMetrics, setK8sMetrics] = useState<any>({});

  // Common state
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('cpu_usage_percent');

  // Time range and auto-refresh
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '24h',
  });
  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 30000,
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Refs to prevent multiple simultaneous requests
  const loadingHostsRef = useRef(false);
  const loadingMetricsRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load hosts/containers on mount or source change
  useEffect(() => {
    const loadData = async () => {
      if (loadingHostsRef.current) return;
      loadingHostsRef.current = true;

      try {
        if (metricSource === 'infrastructure') {
          const response = await hostApi.getHosts();
          if (response.data && response.data.length > 0) {
            setHosts(response.data);
            // Only set default if there's no current selection
            setSelectedHost(prev => prev || response.data[0].id);
          } else {
            setHosts([]);
          }
        } else {
          const response = await kubernetesMetricApi.getContainers();
          if (response.success && response.data && response.data.length > 0) {
            setContainers(response.data);
            // Only set default if there's no current selection
            setSelectedContainer(prev => prev || response.data[0].container_id);
          } else {
            setContainers([]);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        loadingHostsRef.current = false;
      }
    };

    loadData();
  }, [metricSource]); // Only depend on metricSource

  // Load current metrics when host/container changes
  useEffect(() => {
    const loadMetrics = async () => {
      if (loadingMetricsRef.current) return;

      if (metricSource === 'infrastructure' && !selectedHost) return;
      if (metricSource === 'kubernetes' && !selectedContainer) return;

      loadingMetricsRef.current = true;
      setLoading(true);

      try {
        if (metricSource === 'infrastructure') {
          const response = await infrastructureMetricApi.getCurrent(selectedHost);
          if (response && response.data) {
            const metricsData = response.data.data || response.data;
            setInfraMetrics(metricsData);
            // Only set default metric if there's none selected
            setSelectedMetric(prev => prev || 'cpu_usage_percent');
            setLastUpdated(new Date());
          }
        } else {
          const response = await kubernetesMetricApi.getCurrent(selectedContainer);
          if (response && response.data) {
            const metricsData = response.data.data || response.data;
            setK8sMetrics(metricsData);
            // Only set default metric if there's none selected
            setSelectedMetric(prev => prev || 'cpu_usage_percent');
            setLastUpdated(new Date());
          }
        }
      } catch (error) {
        console.error('Error loading metrics:', error);
      } finally {
        setLoading(false);
        loadingMetricsRef.current = false;
      }
    };

    loadMetrics();
  }, [metricSource, selectedHost, selectedContainer]); // Don't include selectedMetric

  // Load time series with debounce
  useEffect(() => {
    if (!selectedMetric) return;
    if (metricSource === 'infrastructure' && !selectedHost) return;
    if (metricSource === 'kubernetes' && !selectedContainer) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the request
    timeoutRef.current = setTimeout(async () => {
      setLoadingTimeSeries(true);

      try {
        if (metricSource === 'infrastructure') {
          const response = await infrastructureMetricApi.getTimeSeries(
            selectedHost,
            selectedMetric,
            timeRange.startTime,
            timeRange.endTime
          );
          if (response && response.data) {
            const dataArray = response.data.data || response.data;
            // Reduce data points for better performance
            const reducedData = reduceDataPoints(dataArray, 200);
            console.log(`Reduced data points from ${dataArray.length} to ${reducedData.length}`);
            setTimeSeriesData(reducedData);
          }
        } else {
          const response = await kubernetesMetricApi.getTimeSeries(
            selectedContainer,
            selectedMetric,
            timeRange.startTime,
            timeRange.endTime
          );
          if (response && response.data) {
            const dataArray = response.data.data || response.data;
            // Reduce data points for better performance
            const reducedData = reduceDataPoints(dataArray, 200);
            console.log(`Reduced data points from ${dataArray.length} to ${reducedData.length}`);
            setTimeSeriesData(reducedData);
          }
        }
      } catch (error) {
        console.error('Error loading time series:', error);
        setTimeSeriesData([]);
      } finally {
        setLoadingTimeSeries(false);
      }
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [selectedMetric, metricSource, selectedHost, selectedContainer, timeRange.startTime, timeRange.endTime]);

  // Auto-refresh - use ref to avoid recreating interval
  const timeRangeRef = useRef(timeRange);

  useEffect(() => {
    timeRangeRef.current = timeRange;
  }, [timeRange]);

  useEffect(() => {
    if (!autoRefresh.enabled) return;

    const interval = setInterval(() => {
      const currentTimeRange = timeRangeRef.current;
      if (currentTimeRange.rangeType === 'preset' && currentTimeRange.presetValue) {
        const endTime = Date.now();
        const ranges: Record<string, number> = {
          '15m': 15 * 60 * 1000,
          '1h': 60 * 60 * 1000,
          '6h': 6 * 60 * 60 * 1000,
          '12h': 12 * 60 * 60 * 1000,
          '24h': 24 * 60 * 60 * 1000,
          '7d': 7 * 24 * 60 * 60 * 1000,
          '30d': 30 * 24 * 60 * 60 * 1000,
        };
        const startTime = endTime - (ranges[currentTimeRange.presetValue] || ranges['1h']);
        setTimeRange({
          ...currentTimeRange,
          startTime,
          endTime,
        });
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh.enabled, autoRefresh.interval]); // Don't depend on timeRange!

  // Memoize chart data to avoid unnecessary re-renders
  const chartData = useMemo(() => timeSeriesData, [timeSeriesData]);

  // Available metrics based on source
  const availableMetrics = useMemo(() => {
    return metricSource === 'kubernetes'
      ? [
          'cpu_usage_percent',
          'cpu_usage_nanocores',
          'memory_usage_percent',
          'memory_used_bytes',
          'memory_limit_bytes',
          'memory_working_set_bytes',
          'network_rx_bytes',
          'network_tx_bytes',
          'network_rx_packets',
          'network_tx_packets',
          'block_io_read_bytes',
          'block_io_write_bytes'
        ]
      : [
          'cpu_usage_percent',
          'memory_usage_percent',
          'disk_usage_percent',
          'disk_io_read_bytes',
          'disk_io_write_bytes',
          'network_rx_bytes',
          'network_tx_bytes',
          'load_avg_1m',
          'load_avg_5m',
          'load_avg_15m'
        ];
  }, [metricSource]);

  const formatMetricValue = (metric: string, value: any) => {
    if (!value && value !== 0) return '-';

    if (metric.includes('percent')) {
      return `${value.toFixed(2)}%`;
    } else if (metric.includes('bytes')) {
      const mb = value / (1024 * 1024);
      if (mb >= 1024) {
        return `${(mb / 1024).toFixed(2)} GB`;
      }
      return `${mb.toFixed(2)} MB`;
    } else if (metric.includes('load_avg')) {
      return value.toFixed(2);
    }
    return value.toFixed(2);
  };

  const getMetricLabel = (metric: string) => {
    const translationKey = `metrics:metricLabels.${metric}`;
    return t(translationKey, { defaultValue: metric });
  };

  // Reduce data points if there are too many (performance optimization)
  const reduceDataPoints = (data: any[], maxPoints: number = 200) => {
    if (data.length <= maxPoints) return data;

    const step = Math.ceil(data.length / maxPoints);
    return data.filter((_, index) => index % step === 0);
  };

  const handleManualRefresh = async () => {
    // Force reload without changing states that trigger effects
    loadingMetricsRef.current = false;
    loadingHostsRef.current = false;

    // Trigger re-fetch by toggling a timestamp
    setLastUpdated(new Date());

    // Reload based on current source
    if (metricSource === 'infrastructure' && selectedHost) {
      const response = await infrastructureMetricApi.getCurrent(selectedHost);
      if (response && response.data) {
        setInfraMetrics(response.data.data || response.data);
      }
    } else if (metricSource === 'kubernetes' && selectedContainer) {
      const response = await kubernetesMetricApi.getCurrent(selectedContainer);
      if (response && response.data) {
        setK8sMetrics(response.data.data || response.data);
      }
    }
  };

  const handleTimeRangeChange = (newTimeRange: TimeRange) => {
    // Auto-select first host/container if none selected
    if (metricSource === 'infrastructure' && !selectedHost && hosts.length > 0) {
      setSelectedHost(hosts[0].id);
    } else if (metricSource === 'kubernetes' && !selectedContainer && containers.length > 0) {
      setSelectedContainer(containers[0].container_id);
    }
    setTimeRange(newTimeRange);
  };

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('metrics:title')}</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t('metrics:subtitle')}</p>
      </div>

      {/* Metric Source Selector */}
      <div className="mb-6">
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => {
              setMetricSource('infrastructure');
              setSelectedMetric('cpu_usage_percent');
              setTimeSeriesData([]);
            }}
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              metricSource === 'infrastructure'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            🖥️ {t('metrics:sourceType.infrastructure')}
          </button>
          <button
            onClick={() => {
              setMetricSource('kubernetes');
              setSelectedMetric('cpu_usage_percent');
              setTimeSeriesData([]);
            }}
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              metricSource === 'kubernetes'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            ☸️ {t('metrics:sourceType.kubernetes', 'Kubernetes Containers')}
          </button>
        </div>
      </div>

      {/* Host Selector */}
      {metricSource === 'infrastructure' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('metrics:filters.selectHost')}
          </label>
          {hosts.length === 0 ? (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <div className="flex items-start gap-3">
                <span className="text-yellow-600 dark:text-yellow-400 text-xl">⚠️</span>
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('metrics:noHosts.title', 'No hosts registered')}
                  </p>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    {t('metrics:noHosts.description', 'To view infrastructure metrics, you need to install the Host Agent on your servers.')}
                  </p>
                  <a
                    href="/agents/install"
                    className="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    {t('metrics:noHosts.installAgent', 'Install Host Agent')} →
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <select
              value={selectedHost}
              onChange={(e) => {
                setSelectedHost(e.target.value);
                setSelectedMetric('cpu_usage_percent');
                setTimeSeriesData([]);
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="">{t('metrics:filters.selectHostPlaceholder')}</option>
              {hosts.map((host) => (
                <option key={host.id} value={host.id}>
                  {host.hostname} ({host.ip_address}) - {host.status}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Container Selector */}
      {metricSource === 'kubernetes' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('metrics:filters.selectContainer', 'Select Container')}
          </label>
          {containers.length === 0 ? (
            <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
              <div className="flex items-start gap-3">
                <span className="text-yellow-600 dark:text-yellow-400 text-xl">⚠️</span>
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    {t('metrics:noContainers.title', 'No containers with metrics')}
                  </p>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    {t('metrics:noContainers.description', 'Container metrics are collected by the Host Agent running on servers with Docker. Install the Host Agent on your Docker hosts to see container metrics here.')}
                  </p>
                  <a
                    href="/agents/install"
                    className="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    {t('metrics:noContainers.installAgent', 'Install Host Agent')} →
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <select
              value={selectedContainer}
              onChange={(e) => {
                setSelectedContainer(e.target.value);
                setSelectedMetric('cpu_usage_percent');
                setTimeSeriesData([]);
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
            >
              <option value="">{t('metrics:filters.selectContainerPlaceholder', 'Select a container...')}</option>
              {containers.map((container) => (
                <option key={container.container_id} value={container.container_id}>
                  {container.container_name} ({container.container_id.substring(0, 12)})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Time Range Selector */}
      <div className="mb-6">
        <TimeRangeSelector
          value={timeRange}
          onChange={handleTimeRangeChange}
        />
      </div>

      {/* Auto Refresh Toggle */}
      <div className="mb-6">
        <AutoRefreshToggle
          value={autoRefresh}
          onChange={setAutoRefresh}
          onManualRefresh={handleManualRefresh}
          lastUpdated={lastUpdated}
        />
      </div>

      {/* Metric Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('metrics:filters.selectMetric')}
        </label>
        <select
          value={selectedMetric}
          onChange={(e) => setSelectedMetric(e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
          disabled={availableMetrics.length === 0}
        >
          <option value="">{t('metrics:filters.selectMetric')}</option>
          {availableMetrics.map((name) => (
            <option key={name} value={name}>
              {getMetricLabel(name)}
            </option>
          ))}
        </select>
      </div>

      {/* Time Series Chart */}
      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedMetric ? getMetricLabel(selectedMetric) : t('metrics:chart.selectMetricToView')}
          </h3>
          {chartData.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {chartData.length} data points
            </span>
          )}
        </div>
        {loadingTimeSeries ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis
                dataKey="time"
                stroke="#9CA3AF"
                tick={{ fontSize: 12 }}
                minTickGap={50}
              />
              <YAxis
                stroke="#9CA3AF"
                width={80}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB'
                }}
                isAnimationActive={false}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#6366F1"
                strokeWidth={2}
                name={selectedMetric ? getMetricLabel(selectedMetric) : 'Value'}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[400px] text-gray-500 dark:text-gray-400">
            {selectedMetric ? t('metrics:chart.noDataForTimeRange') : t('metrics:chart.selectMetricToView')}
          </div>
        )}
      </div>

      {/* Current Metrics */}
      {metricSource === 'infrastructure' && Object.keys(infraMetrics).length > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('metrics:currentMetrics.title')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {availableMetrics.map((metric) => {
                const value = (infraMetrics as any)[metric];
                if (value === undefined || value === null) return null;

                return (
                  <div key={metric} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {getMetricLabel(metric)}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatMetricValue(metric, value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {metricSource === 'kubernetes' && Object.keys(k8sMetrics).length > 0 && (
        <div className="mb-8 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('metrics:currentMetrics.title')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availableMetrics.map((metric) => {
                const value = (k8sMetrics as any)[metric];
                if (value === undefined || value === null) return null;

                return (
                  <div key={metric} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {getMetricLabel(metric)}
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatMetricValue(metric, value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
